/**
 * Background music for guest-facing pages: the RSVP form, wedding websites,
 * and anything else that loads this file (ticketed event pages render through
 * form.js, so they get it with the form).
 *
 *   window.QRMusic.set({ url, enabled, ... } | null, { preview })
 *
 * The page hands over the host's saved `backgroundMusic` value every time it
 * renders; set() is idempotent, so calling it on each re-render is fine.
 *
 * WHY IT IS SHAPED THIS WAY
 * - Browsers refuse sound until the visitor has interacted with the page, and
 *   a play() attempted before that fails silently on most phones. So we never
 *   try: the music starts on the guest's FIRST tap or key press anywhere. On a
 *   page with an envelope that first tap is opening the envelope, which is the
 *   moment the host pictured. No coupling to the envelope code is needed, and
 *   pages without one behave sensibly too.
 * - A mute button is always on screen, and a guest who pauses is remembered
 *   (per track), so they are not ambushed again on their next visit.
 * - Nothing is downloaded until playback starts (preload none). A guest who
 *   never taps costs no bandwidth.
 * - Inside someone else's frame (the RSVP form embedded in a wedding site, a
 *   demo phone) it does nothing at all, so a site and its form can never play
 *   two songs at once. The builder preview is the exception: there it shows
 *   the button so the host can audition, but never starts on its own.
 * - Only our own files play: a Storage download URL from our bucket, or a
 *   track from the built-in library under /assets/audio/music/. A public page
 *   must not stream an arbitrary address that a client managed to save.
 */
(function () {
    'use strict';
    if (window.QRMusic) return;

    var TARGET_VOLUME = 0.55;
    var FADE_MS = 1400;
    var PREF_PREFIX = 'qr_music_off:';

    var LABELS = {
        en: { play: 'Play music', pause: 'Pause music' },
        es: { play: 'Reproducir música', pause: 'Pausar música' },
        pt: { play: 'Tocar música', pause: 'Pausar música' },
        fr: { play: 'Lire la musique', pause: 'Mettre la musique en pause' },
        de: { play: 'Musik abspielen', pause: 'Musik pausieren' },
        he: { play: 'הפעלת מוזיקה', pause: 'השהיית מוזיקה' },
        ar: { play: 'تشغيل الموسيقى', pause: 'إيقاف الموسيقى مؤقتًا' },
        it: { play: 'Riproduci musica', pause: 'Metti in pausa la musica' },
        ru: { play: 'Включить музыку', pause: 'Поставить музыку на паузу' },
        nl: { play: 'Muziek afspelen', pause: 'Muziek pauzeren' },
        tr: { play: 'Müziği çal', pause: 'Müziği duraklat' },
        hi: { play: 'संगीत चलाएँ', pause: 'संगीत रोकें' }
    };

    /** A URL we are willing to stream, or null. */
    function safeUrl(raw) {
        if (typeof raw !== 'string') return null;
        var s = raw.trim();
        if (/^\/assets\/audio\/music\/[a-z0-9-]+\.(mp3|m4a)$/.test(s)) return s;
        try {
            var u = new URL(s);
            if (u.protocol === 'https:' && u.hostname === 'firebasestorage.googleapis.com'
                && u.pathname.indexOf('/v0/b/eventrsvp-bb229.') === 0 && u.href.length <= 2048) {
                return u.href;
            }
        } catch (e) { /* not a URL */ }
        return null;
    }

    /** The track to play for a saved value, or null when music is off. */
    function trackUrl(config) {
        if (!config || typeof config !== 'object' || config.enabled !== true) return null;
        return safeUrl(config.url);
    }

    function inForeignFrame() {
        try { return window.self !== window.top; } catch (e) { return true; }
    }

    function labels() {
        var lang = (document.documentElement.lang || 'en').toLowerCase().split('-')[0];
        return LABELS[lang] || LABELS.en;
    }

    function prefKey(url) {
        // The Storage URL carries a token; the path alone identifies the track.
        return PREF_PREFIX + String(url).split('?')[0].slice(-120);
    }
    function guestTurnedOff(url) {
        try { return localStorage.getItem(prefKey(url)) === '1'; } catch (e) { return false; }
    }
    function rememberChoice(url, off) {
        try {
            if (off) localStorage.setItem(prefKey(url), '1');
            else localStorage.removeItem(prefKey(url));
        } catch (e) { /* private mode: the choice lasts for this visit only */ }
    }

    var audio = null;
    var button = null;
    var currentUrl = null;
    var previewMode = false;
    var playing = false;
    var resumeOnReturn = false;
    var fadeRaf = 0;
    var gestureArmed = false;

    function ensureStyles() {
        if (document.getElementById('qr-music-style')) return;
        var css = ''
            + '.qr-music-btn{position:fixed;z-index:2147483000;bottom:calc(16px + env(safe-area-inset-bottom,0px));'
            + 'inset-inline-start:16px;width:44px;height:44px;border-radius:50%;border:1px solid rgba(255,255,255,.55);'
            + 'background:rgba(20,20,24,.62);color:#fff;display:flex;align-items:center;justify-content:center;'
            + 'padding:0;cursor:pointer;box-shadow:0 4px 14px rgba(0,0,0,.22);-webkit-backdrop-filter:blur(8px);'
            + 'backdrop-filter:blur(8px);transition:transform .15s ease,opacity .3s ease;-webkit-tap-highlight-color:transparent}'
            + '.qr-music-btn:hover{transform:scale(1.06)}'
            + '.qr-music-btn:focus-visible{outline:2px solid #fff;outline-offset:2px}'
            + '.qr-music-btn svg{width:20px;height:20px;display:block}'
            + '.qr-music-btn .qr-eq{display:none;align-items:flex-end;gap:2px;height:16px}'
            + '.qr-music-btn .qr-eq i{display:block;width:3px;background:currentColor;border-radius:1px;'
            + 'animation:qr-eq 0.9s ease-in-out infinite}'
            + '.qr-music-btn .qr-eq i:nth-child(2){animation-delay:-.3s}.qr-music-btn .qr-eq i:nth-child(3){animation-delay:-.6s}'
            + '.qr-music-btn.is-playing .qr-eq{display:flex}.qr-music-btn.is-playing .qr-note{display:none}'
            + '@keyframes qr-eq{0%,100%{height:4px}50%{height:16px}}'
            + '@media (prefers-reduced-motion:reduce){.qr-music-btn .qr-eq i{animation:none;height:10px}}'
            + '@media print{.qr-music-btn{display:none}}';
        var style = document.createElement('style');
        style.id = 'qr-music-style';
        style.textContent = css;
        (document.head || document.documentElement).appendChild(style);
    }

    function ensureButton() {
        if (button) return button;
        ensureStyles();
        button = document.createElement('button');
        button.type = 'button';
        button.className = 'qr-music-btn';
        button.setAttribute('data-qr-music', '');
        button.innerHTML = ''
            + '<svg class="qr-note" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">'
            + '<path d="M9 17.5a3 3 0 1 1-2-2.83V5.6a1 1 0 0 1 .76-.97l10-2.5A1 1 0 0 1 19 3.1v11.4a3 3 0 1 1-2-2.83V7.28l-8 2V17.5z"/></svg>'
            + '<span class="qr-eq" aria-hidden="true"><i></i><i></i><i></i></span>';
        button.addEventListener('click', function (e) {
            e.stopPropagation();
            if (playing) {
                pause(true);
                rememberChoice(currentUrl, true);
            } else {
                rememberChoice(currentUrl, false);
                play();
            }
        });
        (document.body || document.documentElement).appendChild(button);
        syncButton();
        return button;
    }

    function syncButton() {
        if (!button) return;
        var L = labels();
        button.classList.toggle('is-playing', playing);
        button.setAttribute('aria-pressed', playing ? 'true' : 'false');
        button.setAttribute('aria-label', playing ? L.pause : L.play);
        button.title = playing ? L.pause : L.play;
    }

    function removeButton() {
        if (button && button.parentNode) button.parentNode.removeChild(button);
        button = null;
    }

    function fadeTo(target, ms, done) {
        if (!audio) return;
        cancelAnimationFrame(fadeRaf);
        var from = audio.volume;
        var start = performance.now();
        function step(now) {
            if (!audio) return;
            var t = Math.min(1, (now - start) / ms);
            // iOS treats volume as read-only (the hardware buttons own it), so
            // this is a no-op there and the track simply starts at full level.
            try { audio.volume = from + (target - from) * t; } catch (e) { /* read-only */ }
            if (t < 1) fadeRaf = requestAnimationFrame(step);
            else if (done) done();
        }
        fadeRaf = requestAnimationFrame(step);
    }

    function play() {
        if (!currentUrl) return;
        if (!audio) {
            audio = new Audio();
            audio.preload = 'none';
            audio.loop = true;
            audio.addEventListener('error', function () {
                // A file that will not play is not worth a broken button.
                console.warn('[QRMusic] track failed to load', audio && audio.error);
                teardown();
            });
        }
        if (audio.src !== new URL(currentUrl, location.href).href) audio.src = currentUrl;
        try { audio.volume = 0; } catch (e) { /* iOS */ }
        var p = audio.play();
        playing = true;
        syncButton();
        if (p && typeof p.then === 'function') {
            p.then(function () { fadeTo(TARGET_VOLUME, FADE_MS); }, function (err) {
                // Blocked (no activation yet) or interrupted by a src swap. Show
                // the play button and wait for the guest's next tap.
                playing = false;
                syncButton();
                if (err && err.name === 'NotAllowedError' && !previewMode) armGesture();
            });
        } else {
            fadeTo(TARGET_VOLUME, FADE_MS);
        }
    }

    function pause(fade) {
        playing = false;
        syncButton();
        if (!audio) return;
        if (fade) {
            var a = audio;
            fadeTo(0, 300, function () { if (!playing) a.pause(); });
        } else {
            cancelAnimationFrame(fadeRaf);
            audio.pause();
        }
    }

    // ---- start on the first interaction -----------------------------------
    var GESTURES = ['click', 'touchend', 'keydown'];
    function onFirstGesture(e) {
        // The music button handles its own taps; Tab-key focus moves are not
        // an invitation to make noise.
        if (e.target && e.target.closest && e.target.closest('[data-qr-music]')) { disarmGesture(); return; }
        if (e.type === 'keydown' && (e.key === 'Tab' || e.key === 'Shift' || e.key === 'Escape')) return;
        disarmGesture();
        if (!currentUrl || playing || guestTurnedOff(currentUrl)) return;
        play();
    }
    function armGesture() {
        if (gestureArmed) return;
        gestureArmed = true;
        GESTURES.forEach(function (t) { document.addEventListener(t, onFirstGesture, true); });
    }
    function disarmGesture() {
        if (!gestureArmed) return;
        gestureArmed = false;
        GESTURES.forEach(function (t) { document.removeEventListener(t, onFirstGesture, true); });
    }

    // A backgrounded tab should not keep singing; pick up again on return.
    document.addEventListener('visibilitychange', function () {
        if (!audio) return;
        if (document.hidden) {
            resumeOnReturn = playing;
            if (playing) pause(false);
        } else if (resumeOnReturn) {
            resumeOnReturn = false;
            play();
        }
    });

    function teardown() {
        disarmGesture();
        if (audio) {
            cancelAnimationFrame(fadeRaf);
            try { audio.pause(); audio.removeAttribute('src'); audio.load(); } catch (e) { /* ignore */ }
        }
        audio = null;
        playing = false;
        currentUrl = null;
        removeButton();
    }

    /**
     * @param {object|null} config  the saved backgroundMusic value
     * @param {{preview?: boolean}} [opts]  preview = a builder's live preview
     */
    function set(config, opts) {
        var preview = !!(opts && opts.preview);
        var url = trackUrl(config);
        if (url && inForeignFrame() && !preview) url = null;

        if (!url) { teardown(); return; }
        if (url === currentUrl && preview === previewMode) {
            ensureButton();
            return;
        }

        var wasPlaying = playing;
        if (audio) pause(false);
        currentUrl = url;
        previewMode = preview;
        ensureButton();
        syncButton();

        if (preview) {
            // The host swapped the song while auditioning: play the new one.
            disarmGesture();
            if (wasPlaying) play();
        } else if (!guestTurnedOff(url)) {
            armGesture();
        }
    }

    function whenBodyReady(fn) {
        if (document.body) fn();
        else document.addEventListener('DOMContentLoaded', fn, { once: true });
    }

    window.QRMusic = {
        set: function (config, opts) { whenBodyReady(function () { set(config, opts); }); },
        /** Fade out and stop (e.g. a buyer moving on to checkout). */
        stop: function () { if (playing) pause(true); disarmGesture(); },
        isPlaying: function () { return playing; },
        /** Start now, from a tap that MEANS "go in" (the site's opening cover).
         *  Unlike the first-gesture listener this also works in the builder
         *  preview, where ordinary clicks must stay silent: a host who taps
         *  "Tap to enter" there expects to hear what guests will. Call it from
         *  inside the tap's own handler so the browser counts the gesture.
         *  A guest who switched the music off before is still left in peace. */
        start: function () {
            if (!currentUrl || playing || (!previewMode && guestTurnedOff(currentUrl))) return;
            disarmGesture();
            play();
        },
        safeUrl: safeUrl,
        trackUrl: trackUrl
    };
})();
